/**
 * The one Zustand store (frontend.md §1.2). `subscribeWithSelector` is here from
 * the start because M3's animation director subscribes to specific transitions
 * from outside React.
 *
 * Only `ui` is persisted. The access token is memory-only by design, and every
 * other slice is either server truth or per-connection state that must not
 * survive a reload as a stale copy.
 */

import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';

import { createAnimSlice } from './anim';
import { createAuthSlice } from './auth';
import { createLobbySlice } from './lobby';
import { createRoomSlice } from './room';
import { createSocialSlice } from './social';
import { createTableSlice } from './table';
import type { AnimationLevel, Slices } from './types';
import { createUiSlice } from './ui';

interface PersistedUi {
  locale: Slices['ui']['locale'];
  animationLevel: AnimationLevel;
  sound: boolean;
  senasCollapsed: boolean;
}

export const useStore = create<Slices>()(
  subscribeWithSelector(
    persist(
      (...a) => ({
        ...createAuthSlice(...a),
        ...createUiSlice(...a),
        ...createLobbySlice(...a),
        ...createRoomSlice(...a),
        ...createTableSlice(...a),
        ...createSocialSlice(...a),
        ...createAnimSlice(...a),
      }),
      {
        name: 'ordago',
        version: 1,
        // Actions are recreated by the slice creators on every boot, so only the
        // few values worth remembering are written.
        partialize: (s): { ui: PersistedUi } => ({
          ui: {
            locale: s.ui.locale,
            animationLevel: s.ui.animationLevel,
            sound: s.ui.sound,
            senasCollapsed: s.ui.senasCollapsed,
          },
        }),
        merge: (persisted, current): Slices => {
          const saved = (persisted as { ui?: Partial<PersistedUi> } | undefined)?.ui;
          return {
            ...current,
            ui: {
              ...current.ui,
              ...(saved?.locale === undefined ? {} : { locale: saved.locale }),
              ...(saved?.animationLevel === undefined
                ? {}
                : { animationLevel: saved.animationLevel }),
              ...(saved?.sound === undefined ? {} : { sound: saved.sound }),
              ...(saved?.senasCollapsed === undefined
                ? {}
                : { senasCollapsed: saved.senasCollapsed }),
            },
          };
        },
      },
    ),
  ),
);

export type { AnimationLevel, AuthStatus, Slices } from './types';
